import { NavLink, useNavigate } from "react-router-dom";
import { Bell, BellRing, LogOut } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { unlockAlerts, alertsUnlocked, playAlert, enablePush, pushEnabled, systemNotify } from "@/lib/alerts";
import { useAuth } from "@/context/AuthContext";
import { useCityTheme } from "@/context/CityThemeContext";
import { CityBrandWordmark, CityLogo, TimeFoodMark } from "@/components/Brand";
import { toast } from "sonner";

/** Sons de notificação: jingle (novo pedido/corrida) e moedinha (comissão). */
const playSound = (id) => {
  try {
    const el = document.getElementById(id);
    if (el) {
      el.currentTime = 0;
      el.volume = 0.85;
      el.play().catch(() => {});
    }
  } catch { /* ignore */ }
};

export const playJingle = () => playSound("tf-jingle");
export const playCoin = () => playSound("tf-coin");

export const JingleAudio = () => (
  <>
    <audio id="tf-jingle" src="/sounds/jingle.mp3" preload="auto" data-testid="jingle-audio" />
    <audio id="tf-coin" src="/sounds/coin.mp3" preload="auto" data-testid="coin-audio" />
  </>
);

export const MobileShell = ({ children, nav, accent = "#FF7A1A" }) => {
  useEffect(() => {
    // Destrava o som no primeiro toque do usuário em qualquer lugar do app.
    const handler = () => { if (!alertsUnlocked()) unlockAlerts(); };
    window.addEventListener("pointerdown", handler, { once: false });
    return () => window.removeEventListener("pointerdown", handler);
  }, []);
  return (
    <div className="min-h-screen bg-gf-gray">
      <div className="mx-auto w-full max-w-[520px] bg-white min-h-screen shadow-xl relative pb-24">
        {children}
        <BottomNav items={nav} accent={accent} />
      </div>
    </div>
  );
};

export const BottomNav = ({ items, accent }) => (
  <nav
    data-testid="bottom-nav"
    className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[520px] bg-white border-t border-gray-200 z-40"
  >
    <div className="flex">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          data-testid={it.testId}
          className="flex-1 flex flex-col items-center gap-1 py-3 text-[10px] font-semibold"
          style={({ isActive }) => ({ color: isActive ? accent : "#8A8A8A" })}
        >
          {({ isActive }) => (
            <>
              <it.icon size={20} strokeWidth={isActive ? 2.6 : 2} />
              {it.label}
            </>
          )}
        </NavLink>
      ))}
    </div>
  </nav>
);

export const TopBar = ({ subtitle, right, institutional = false }) => {
  const { logout } = useAuth();
  const { city, cityName, accent } = useCityTheme();
  const navigate = useNavigate();
  return (
    <header
      data-testid="top-bar"
      className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-gray-200 px-4 py-3 flex items-center justify-between"
    >
      <div className="flex items-center gap-2">
        {institutional && <CityLogo city={city} size={30} showText={false} />}
        <div className="leading-tight">
          <CityBrandWordmark city={cityName} size={17} accent={accent} />
          {subtitle && <div className="text-[11px] text-gray-500">{subtitle}</div>}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {right}
        <button
          data-testid="logout-button"
          onClick={async () => { await logout(); navigate("/login"); }}
          className="p-2 rounded-full hover:bg-gf-gray gf-press"
          title="Sair"
        >
          <LogOut size={19} className="text-gray-600" />
        </button>
      </div>
    </header>
  );
};

/** Botão para ligar som + notificação no celular (exige toque do usuário). */
export const EnableAlertsButton = ({ accent = "#FF7A1A" }) => {
  const [on, setOn] = useState(alertsUnlocked() && pushEnabled());
  const [busy, setBusy] = useState(false);

  const enable = async () => {
    setBusy(true);
    const sound = await unlockAlerts();
    const push = await enablePush();
    setBusy(false);
    setOn(sound);
    if (sound) await playAlert("jingle");
    if (sound && push.ok) toast.success("Alertas ligados: som + notificação no celular");
    else if (sound) toast.warning(`Som ligado. Notificação no celular: ${push.reason}`);
    else toast.error("Não foi possível ligar o som. Toque novamente na tela e tente de novo.");
  };

  if (on) {
    return (
      <button data-testid="alerts-enabled-badge" onClick={enable} title="Alertas ligados — toque para testar o som"
        className="p-2 rounded-full gf-press">
        <BellRing size={19} style={{ color: accent }} />
      </button>
    );
  }
  return (
    <button data-testid="enable-alerts-button" onClick={enable} disabled={busy}
      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-bold text-white gf-press animate-pulse-ring"
      style={{ background: accent }}>
      <BellRing size={14} /> Ligar alertas
    </button>
  );
};

export const NotificationBell = ({ accent = "#FF7A1A" }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const seen = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      const ids = new Set(data.map((i) => i.id));
      if (seen.current) {
        // Som apenas em: novo pedido (lojista), nova corrida (motoboy) e comissão (moedinha)
        const fresh = data.filter((i) => !seen.current.has(i.id) && i.sound === true && !i.read);
        if (fresh.length) {
          const coin = fresh.find((i) => i.sound_type === "coin");
          playAlert(coin ? "coin" : "jingle");
          const first = fresh[0];
          systemNotify(first.title, first.body);
        }
      }
      seen.current = ids;
      setItems(data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const unread = items.filter((i) => !i.read).length;
  return (
    <div className="relative">
      <button
        data-testid="notifications-button"
        onClick={async () => {
          setOpen((o) => !o);
          if (!open && unread) { await api.post("/notifications/read-all"); load(); }
        }}
        className="p-2 rounded-full hover:bg-gf-gray gf-press relative"
      >
        <Bell size={19} className="text-gray-700" />
        {unread > 0 && (
          <span
            data-testid="notifications-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] text-[10px] font-bold text-white rounded-full flex items-center justify-center px-1"
            style={{ background: accent }}
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notifications-panel"
          className="absolute right-0 mt-2 w-[300px] max-h-[360px] overflow-auto bg-white rounded-2xl shadow-2xl border border-gray-200 z-50"
        >
          {items.length === 0 && <p className="p-4 text-sm text-gray-500">Nenhuma notificação.</p>}
          {items.map((n) => (
            <div key={n.id} className="p-3 border-b border-gray-100 last:border-0">
              <p className="text-sm font-semibold text-gf-black">{n.title}</p>
              <p className="text-xs text-gray-600">{n.body}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">
                {new Date(n.created_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const Loader = () => (
  <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-white">
    <div className="animate-pulse"><TimeFoodMark size={72} /></div>
    <p className="text-sm text-gray-500">Carregando…</p>
  </div>
);
